/**
 * Shared types used across both main and renderer processes
 * This file contains types that are shared for IPC communication
 */

// ===== API Response Types =====
import { User as SupabaseAuthUser } from '@supabase/supabase-js';

export interface ApiResponse<T = undefined> {
  success: boolean;
  error?: string;
  data?: T;
}

export interface ReportCsvExportColumn {
  key: string;
  header: string;
}

export type ReportCsvExportValue = string | number | boolean | null;

export type ReportCsvExportRow = Record<string, ReportCsvExportValue>;

export interface ExportReportCsvRequest {
  title: string;
  fileName: string;
  columns: ReportCsvExportColumn[];
  rows: ReportCsvExportRow[];
}

export interface ExportReportCsvResponse {
  filePath: string;
}

// ===== Auth Types =====

export enum UserRole {
  SUPER = 'super',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
  MANAGER = 'manager',
  WAITER = 'waiter',
  CASHIER = 'cashier',
  RECEPTIONIST = 'receptionist',
  ACCOUNTANT = 'accountant',
  KDS = 'kds',
}

// ===== Company Types =====

export enum BusinessType {
  RESTAURANT_BAR = 'restaurant_bar',
  RETAIL = 'retail',
  OTHER = 'other',
}

export enum SubscriptionPackage {
  STARTER = 'starter',
  PROFESSIONAL = 'professional',
  ENTERPRISE = 'enterprise',
  PRO_SEMI_ANNUAL = 'pro_semi_annual',
}

export type BillingCycle = 'monthly' | 'semi_anually' | 'annual';

export interface Company {
  id: number;
  business_name: string;
  business_type: BusinessType;
  country_id: number;
  country_iso: string;
  has_fiscal_integration: boolean;
  currency_prefix?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Outlet {
  id: string;
  name: string;
  address: string | null;
  tin_number: string | null;
  phone: string | null;
  currency?: string | null;
  currency_prefix?: string | null;
  receipt_footnote: string | null;
  day_open: string | null;
  business_type: BusinessType;
  company_id: number;
  kot_tracking_enabled: boolean;
  is_kds_enabled: boolean;
  /** True only when this outlet's EBM device is ACTIVE (and the country
   *  supports EBM). Gates settlement-time fiscal enforcement. */
  is_ebm_active?: boolean;
  created_at: string;
  updated_at: string;
}

// active, trialing, past_due, canceled, incomplete
export enum SubscriptionStatus {
  ACTIVE = 'active',
  TRIALING = 'trialing',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  INCOMPLETE = 'incomplete',
}

export interface CompanySubscription {
  id: string;
  package: SubscriptionPackage;
  billing_cycle?: BillingCycle;
  stripe_environment?: 'development' | 'production' | null;
  trial_ends_at: string;
  stripe_customer_id: string | null;
  subscription_status: SubscriptionStatus;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  current_period_start: string | null;
  grace_period_ends_at?: string | null;
  suspended_at?: string | null;
  data_purge_scheduled_for?: string | null;
  last_payment_reminder_sent_at?: string | null;
  last_purge_warning_sent_at?: string | null;
  payment_reminder_count?: number;
  last_stripe_sync_at?: string | null;
  created_at: string;
  updated_at: string;
}

export type UserOnboardingStatus =
  | 'not_started'
  | 'in_progress'
  | 'completed'
  | 'skipped';

export type UserOnboardingStepId =
  | 'outlets'
  | 'departments'
  | 'menu'
  | 'sections_tables'
  | 'section_printers'
  | 'takeaway_printers'
  | 'first_order'
  | 'users'
  | 'security';

export interface UserOnboardingProgress {
  flow_key: string;
  status: UserOnboardingStatus;
  completed_steps: UserOnboardingStepId[];
  current_step: UserOnboardingStepId | null;
  started_at: string;
  completed_at: string | null;
  last_seen_at: string;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: UserRole;
  outlet_id: string | null;
  is_active: boolean;
  invited_by?: string | null;
  disabled_at?: string | null;
  disabled_by?: string | null;
  created_at: string;
  updated_at: string;
  company: Company | null;
  outlet: Outlet | null;
  subscription: CompanySubscription | null;
  onboarding: UserOnboardingProgress | null;
  login_code?: string | null;
  has_passcode?: boolean;
}

export interface SignupData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  businessName: string;
  businessType: string;
  countryId: number;
  package: string;
}

export interface SignupResponse {
  success: boolean;
  user?: Partial<User>;
  companyId?: number;
  outletId?: string;
  error?: string;
  requiresEmailVerification?: boolean;
}

export interface SupabaseUser extends SupabaseAuthUser {}

export interface LoginResponse {
  success: boolean;
  user?: User;
  error?: string;
}

export interface VerifyEmailResponse {
  success: boolean;
  error?: string;
  user?: SupabaseUser;
}

export interface SetupAccountResponse {
  success: boolean;
  user?: Partial<User>;
  companyId?: number;
  outletId?: string;
  trialEndsAt?: string;
  error?: string;
}

export interface BillingRecord {
  id: string;
  outlet_id?: string | null;
  company_id?: number;
  package: SubscriptionPackage;
  billing_cycle?: BillingCycle;
  stripe_environment?: 'development' | 'production' | null;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  trial_ends_at?: string;
  subscription_status:
    | 'active'
    | 'trialing'
    | 'past_due'
    | 'canceled'
    | 'incomplete';
  current_period_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
  canceled_at?: string | null;
  grace_period_ends_at?: string | null;
  suspended_at?: string | null;
  data_purge_scheduled_for?: string | null;
  last_payment_reminder_sent_at?: string | null;
  last_purge_warning_sent_at?: string | null;
  payment_reminder_count?: number;
  last_stripe_sync_at?: string | null;
  created_at: string;
  updated_at: string;
}

// ===== Config Types =====

export interface SupportedCountry {
  id: number;
  name: string;
  currency_prefix: string;
  is_supported: boolean;
  created_at: string;
  updated_at: string;
}

// ===== Onboarding Types =====

export interface OnboardingFormData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  businessName: string;
  businessType: BusinessType;
  countryId: number;
  package: SubscriptionPackage;
  billingCycle: BillingCycle;
}

export enum OnboardingStep {
  PERSONAL_INFO = 0,
  EMAIL_VERIFICATION = 1,
  BUSINESS_INFO = 2,
  PACKAGE_SELECTION = 3,
  PAYMENT_SETUP = 4,
  COMPLETE = 5,
}

export interface OnboardingState {
  currentStep: OnboardingStep;
  formData: Partial<OnboardingFormData>;
  isLoading: boolean;
  error: string | null;
  isComplete: boolean;
  pendingUserId?: string; // User ID for email verification
}

// ===== Component Props Types =====

export interface PersonalInfoData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface BusinessInfoData {
  businessName: string;
  businessType: BusinessType;
  countryId: number;
}

export interface SelectOption {
  value: string;
  label: string;
}

export type APIRequestKey =
  | 'config:getSupportedCountries'
  | 'departments:getAll'
  | 'departments:getByOutlet'
  | 'departments:create'
  | 'departments:update'
  | 'departments:delete'
  | 'categories:getByOutlet'
  | 'categories:create'
  | 'categories:update'
  | 'categories:delete'
  | 'menuItems:getByOutlet'
  | 'menuItems:getByCategory'
  | 'menuItems:create'
  | 'menuItems:update'
  | 'menuItems:delete'
  | 'addons:getByOutlet'
  | 'addons:create'
  | 'addons:update'
  | 'addons:delete'
  | 'itemAddons:getByItem'
  | 'itemAddons:add'
  | 'itemAddons:update'
  | 'itemAddons:remove'
  | 'auth:register'
  | 'auth:verifyEmail'
  | 'auth:setupAccount'
  | 'auth:login'
  | 'auth:logout'
  | 'auth:getCurrentUser'
  | 'auth:googleSignIn';
